import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { PROFILE } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

function Stars() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, t = 0;
    const stars = [];
    const N = 90;
    const resize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    resize();
    for (let i = 0; i < N; i++) {
      stars.push({ x: Math.random() * w, y: Math.random() * h * 0.85, r: Math.random() * 1.3 + 0.2, p: Math.random() * Math.PI * 2 });
    }
    const draw = () => {
      t += 0.02;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        const a = 0.35 + Math.sin(t + s.p) * 0.35;
        ctx.fillStyle = `rgba(255,255,255,${Math.max(0, a)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
}

function AuroraBg() {
  return (
    <div className="absolute inset-0">
      <div className="aurora-wrap">
        <div className="aurora-band aurora-1" />
        <div className="aurora-band aurora-2" />
        <div className="aurora-band aurora-3" />
      </div>
      <Stars />
      <div className="absolute inset-0 bg-gradient-to-b from-midnight/20 via-transparent to-midnight" />
    </div>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const h = t.hero;
  return (
    <section id="home" className="relative min-h-screen flex items-end overflow-hidden pt-24 pb-16 bg-midnight text-paper">
      <AuroraBg />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono-tech text-xs uppercase tracking-[0.25em] text-aurora mb-6" data-testid="hero-overline">
            {h.overline}
          </motion.p>

          <h1 className="font-display font-extrabold leading-[0.95] tracking-tight text-4xl md:text-6xl lg:text-[4.6rem]" data-testid="hero-headline">
            <MaskLine delay={0.1}>{h.headline.line1}</MaskLine>
            <MaskLine delay={0.22}>
              {h.headline.line2Prefix}<span className="font-serif-accent italic font-medium text-aurora">{h.headline.line2Highlight}</span>
            </MaskLine>
            <MaskLine delay={0.34}>
              {h.headline.line3Prefix}<span className="font-serif-accent italic font-medium text-auroraCyan">{h.headline.line3Highlight}</span>
            </MaskLine>
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="mt-6 font-serif-accent italic text-xl md:text-2xl text-paper/80">
            {h.subtitle}
          </motion.p>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }} className="mt-8 max-w-xl text-lg font-light text-paper/60 leading-relaxed">
            {h.description}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <a href="#experience" onClick={(e) => { e.preventDefault(); const el = document.getElementById("experience"); window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el?.scrollIntoView({ behavior: "smooth" }); }} className="group inline-flex items-center gap-2 rounded-full bg-paper text-midnight px-7 py-3.5 font-mono-tech text-xs uppercase tracking-[0.14em] hover:-translate-y-1 transition-transform duration-300" data-testid="hero-explore-btn">
              {h.exploreBtn} <ArrowDownRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </a>
            <a href="#product" onClick={(e) => { e.preventDefault(); const el = document.getElementById("product"); window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el?.scrollIntoView({ behavior: "smooth" }); }} className="inline-flex items-center gap-2 rounded-full border border-paper/40 px-7 py-3.5 font-mono-tech text-xs uppercase tracking-[0.14em] hover:bg-paper/10 transition-colors duration-300" data-testid="hero-product-btn">
              {h.productBtn}
            </a>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="lg:col-span-4 hidden lg:block">
          <div className="relative">
            <div className="aspect-[3/4] overflow-hidden border border-paper/15 grayscale contrast-[1.05]">
              <img src={PROFILE.portrait} alt={PROFILE.name} className="w-full h-full object-cover" data-testid="hero-portrait" />
            </div>
            <div className="absolute -bottom-3 -left-3 bg-aurora text-midnight px-4 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em]">
              {PROFILE.name}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
